import React, { useCallback, useEffect, useState } from "react";

import { attachFarm, detachFarm, getAllPartners, getPartnerFarmers } from "services";

import { useCancelAsyncOnUnmount } from "utils";

import { AdminSection, AdminTable, Button, Col, Modal, PageSpin, Row, Typography } from "components";

export const AttachFarmers = ({ partner, onUpdateSuccess }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [resultMessage, setResultMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState(true);
  const [farmers, setFarmers] = useState({
    attached: [],
    unattached: [],
  });
  const signal = useCancelAsyncOnUnmount()();

  const loadPartners = useCallback(() => {
    setLoading(true);
    getAllPartners({
      signal,
    }).then((res) => {
      setPartners(res.partners);
    });
  }, [signal]);

  const loadFarmers = useCallback(
    () =>
      getPartnerFarmers({
        partnerId: partner.id,
        signal,
      }).then((res) => {
        setFarmers(res);
      }),
    [signal, partner.id]
  );

  const handleAttachFarmer = (assignPastCredits) => {
    if (selectedFarmer) {
      setLoading(true);
      attachFarm({
        farmId: selectedFarmer.id,
        partnerId: partner.id,
        assignPastCredits,
        signal,
      })
        .then((res) => {
          const { created, otherPartnerAssignments, updatedAssignments } = res;
          if (created !== 0 || otherPartnerAssignments !== 0 || updatedAssignments !== 0) {
            setResultMessage(res);
          } else {
            setShowModal(false);
            onUpdateSuccess();
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleOpenModalConfirm = (farmId) => {
    const farmer = farmers.unattached?.find((farmer) => farmer.id === farmId);
    setSelectedFarmer(farmer);
    if (farmer?.state?.unassigned === 0 && farmer?.state?.assigned === 0) {
      handleAttachFarmer(false);
      return;
    }
    setShowModal(true);
  };

  const handleDetachFarmer = (farmId) => {
    detachFarm({
      farmId,
      signal,
    })
      .then(() => {
        onUpdateSuccess();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    Promise.all([loadFarmers(), loadPartners()]).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return loading ? (
    <PageSpin />
  ) : (
    <>
      <Row style={{ margin: "2.5rem 0 0" }}>
        <Col>
          <Typography variant="paragraph-m">
            You can attach and detach farmers to {partner.name} here. When an attached farmer applies a new method to a field, it will automatically be assigned
            to this partner. The farm's hectare limit will also be overridden by the hectare limit of this partner.
          </Typography>
        </Col>
      </Row>
      <Row>
        <Col sm={24} md={12}>
          <AdminSection headline="Unattached farmers" />
          {!!farmers.unattached.length && (
            <AdminTable
              dataSource={farmers.unattached}
              config={{
                exclude: ["partnerId", "state"],
                actions: [
                  {
                    icon: "klim/plus",
                    func: handleOpenModalConfirm,
                    arguments: ["id"],
                    tooltip: "Assign this farmer",
                  },
                  {
                    icon: "tabler/eye",
                    route: "/farm/",
                    tooltip: "View this farm",
                  },
                ],
              }}
            />
          )}
        </Col>
        {!!farmers.attached.length && (
          <Col sm={24} md={12}>
            <AdminSection headline="Attached farmers" />

            <AdminTable
              dataSource={farmers.attached.filter((farmer) => farmer.partnerId === partner.id)}
              config={{
                exclude: ["partnerId"],
                actions: [
                  {
                    icon: "tabler/trash",
                    func: handleDetachFarmer,
                    arguments: ["id"],
                  },
                  {
                    icon: "tabler/eye",
                    route: "/farm/",
                  },
                ],
              }}
            />
          </Col>
        )}
      </Row>
      <Modal
        okText={"close"}
        cancelText=" "
        description={
          <>
            {!resultMessage && (
              <>
                <b>
                  This farmer has {selectedFarmer?.state?.unassigned} assignable credits. <br></br>
                  Would you like to assign all available credits to this partner?<br></br>
                  The credit assignment feature is under construction, therefore no assignments are possible at the moment.<br></br>
                </b>
                {selectedFarmer?.state?.assigned > 0 && (
                  <b>
                    Additionally, the farmer has {selectedFarmer?.state?.assigned} credits assigned to other partners. If intended, these need to be manually
                    unassigned.
                  </b>
                )}
                <div className="margin-top-200">
                  <Row>
                    <Col sm={12} md={12}>
                      <Button loading={loading} onClick={() => handleAttachFarmer(true)} type="primary" size="small" disabled>
                        yes
                      </Button>
                    </Col>
                    <Col sm={12} md={12}>
                      <Button loading={loading} onClick={() => handleAttachFarmer(false)} type="primary" size="small" className="margin-left-100">
                        no
                      </Button>
                    </Col>
                  </Row>
                </div>
              </>
            )}
            {resultMessage && (
              <>
                {resultMessage.updatedAssignments > 0 && (
                  <Typography variant="paragraph-m" color="white">{`Updated ${resultMessage.updatedAssignments} assignments.`}</Typography>
                )}
                {resultMessage.created && <Typography variant="paragraph-m" color="white">{`Created ${resultMessage.created} assignments.`}</Typography>}
                {resultMessage.otherPartnerAssignments && (
                  <Typography
                    variant="paragraph-m"
                    color="white"
                  >{`${resultMessage.otherPartnerAssignments} credit assignments to other partners were found in:`}</Typography>
                )}
                {resultMessage.otherPartners.length > 0 && (
                  <>
                    {resultMessage.otherPartners.map((partnerId) => {
                      const foundPartner = partners?.find((partner) => partner.id === partnerId);
                      return (
                        <>
                          <b className="white-text" key={partnerId}>
                            {foundPartner.name} - {foundPartner.id}
                          </b>
                          <br></br>
                        </>
                      );
                    })}
                  </>
                )}
              </>
            )}
          </>
        }
        onOk={() => {
          setShowModal(false);
        }}
        showModal={showModal}
        title={"Add farmer modules"}
      />
    </>
  );
};
